import { ArrowUpRight, ChevronDown } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { useIsMobile } from '@worldcoo/ui/hooks/use-mobile';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useState } from 'react';
import { LanguageFlag, LANGUAGES } from './languages';
import { useNavLinks } from './nav';
import { NavStroke } from './primitives';
import { MobileMenu } from './mobile-menu';

const XL_BREAKPOINT = 1280;

/**
 * A NATIVE `<select>` wearing the hand-off's trigger.
 *
 * The list has to be the platform's — that is what gives a phone its proper picker — but an `<option>`
 * may only contain text, so the flag could never live inside one. The designed trigger is therefore
 * rendered normally and the real control is stretched over it at zero opacity: the visitor sees the
 * flag and code, and gets the OS list on tap. `opacity-0` rather than `sr-only`, because a select that
 * is not laid out cannot be clicked.
 */
function LanguageSelector() {
	const { t } = useLingui();
	const { language, changeLanguage } = useTranslation();
	const current = LANGUAGES.find((l) => l.code === language) ?? LANGUAGES[0];
	return (
		<div className="text-brand-nav relative flex items-center gap-1.5 rounded-full px-1 py-1">
			<LanguageFlag code={current.code} className="size-5 shrink-0" />
			<span className="font-medium">{current.short}</span>
			<ChevronDown className="size-4" />
			<select
				aria-label={t`Language`}
				value={current.code}
				onChange={(event) => {
					// Looked up rather than cast: `event.target.value` is a plain string, and the lookup is
					// what proves it is one of ours before it reaches `changeLanguage`.
					const next = LANGUAGES.find((lang) => lang.code === event.target.value);
					if (next) changeLanguage(next.code);
				}}
				className="absolute inset-0 cursor-pointer opacity-0"
			>
				{LANGUAGES.map((lang) => (
					<option key={lang.code} value={lang.code}>
						{lang.label}
					</option>
				))}
			</select>
		</div>
	);
}

export function Header() {
	const { t } = useLingui();
	const { language, changeLanguage } = useTranslation();
	const isMobile = useIsMobile(XL_BREAKPOINT);
	const navLinks = useNavLinks();
	const [menuOpen, setMenuOpen] = useState(false);

	return (
		<header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md">
			<div className="relative mx-auto flex h-[95px] max-w-[1440px] items-center px-6 xl:px-10">
				<Link to="/www" activeOptions={{ exact: true }} className="shrink-0">
					<span className="sr-only">{t`Worldcoo home`}</span>
					<WorldcooLogo className="h-11 w-auto" accentClassName="text-brand" />
				</Link>

				<nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-6 xl:flex">
					{navLinks.map((item) => (
						<Link key={item.to} to={item.to} className="group px-2 py-1 font-medium">
							{/* The current page is marked with the brand stroke, not by recolouring the label.
							    The span is what the stroke is measured against, so it spans the word and not
							    the link's padding; hover previews the same stroke at half strength.

							    Opacity is left to say how strong the mark is and the nib to say whether it is
							    there at all — an undrawn nib is already invisible, so there is no `opacity-0`
							    resting state to fade through. Leaving a hover un-signs it, which is the same
							    gesture run backwards. */}
							<span className="relative inline-block">
								{item.label}
								<NavStroke
									className="opacity-50 group-aria-[current=page]:opacity-100"
									penClassName="transition-[stroke-dashoffset] duration-500 ease-out group-hover:[stroke-dashoffset:0] group-aria-[current=page]:[stroke-dashoffset:0] motion-reduce:transition-none"
								/>
							</span>
						</Link>
					))}
				</nav>

				<div className="ml-auto hidden items-center gap-4 xl:flex">
					<LanguageSelector />
					<Link
						to="/www/contact"
						className="bg-brand hover:bg-brand-strong flex items-center gap-2 rounded-full py-2.5 pr-2.5 pl-4 font-medium text-black transition-colors"
					>
						{t`Contact us`}
						<span className="bg-ink grid size-6 place-items-center rounded-full text-white">
							<ArrowUpRight className="size-4" />
						</span>
					</Link>
				</div>

				{/* Mounted OUTRIGHT, not on the first tap. It used to sit behind a `lazy()` and a `menuMounted`
				    latch because the sheet was `vaul`, ~10.4KB brotli that most visitors never opened. The sheet is
				    now the snap sheet on Radix's Dialog, which the first-paint chunk already carries, so the whole
				    module is ~2KB brotli over shared vendors — less than the tap it was costing, which is the one
				    moment on a phone where a round-trip is felt. `isMobile` still gates it: `useIsMobile` reports
				    false on the server and on the hydrating render, so the burger arrives with hydration either way. */}
				{isMobile && (
					<MobileMenu language={language} changeLanguage={changeLanguage} open={menuOpen} onOpenChange={setMenuOpen} />
				)}
			</div>
		</header>
	);
}
